import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { tagService } from '../services/api';
import Spinner from '../components/Spinner';
import PaginationControls from '../components/PaginationControls';
import { Helmet } from 'react-helmet-async';

const TagView = () => {
  const { slug } = useParams();
  const [tag, setTag] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const fetchTagData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await tagService.getTagBySlug(slug, {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        sortBy: 'publishedAt',
        sortOrder: 'DESC'
      });

      if (response.success) {
        setTag(response.data.tag);
        setPosts(response.data.posts);
        setPagination(response.data.pagination);
      } else {
        setError('Tag não encontrada ou ocorreu um erro ao carregá-la');
      }
    } catch (error) {
      console.error('Erro ao carregar tag:', error);
      setError('Ocorreu um erro ao carregar a tag. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [slug, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    fetchTagData();
  }, [fetchTagData]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !tag) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-800">Erro</h1>
        <p className="text-red-500">{error || 'Tag não encontrada'}</p>
        <Link to="/" className="text-blue-600 hover:underline block mt-4">
          Voltar para a página inicial
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Posts com tag: {tag.name} | Estudos Igreja de Jardim</title>
        <meta name="description" content={`Lista de estudos com a tag ${tag.name}${tag.description ? ` - ${tag.description}` : ''}`} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <header className="mb-8 border-b pb-4">
          <div className="flex items-center mb-2">
            <span 
              className="w-4 h-4 rounded-full mr-2" 
              style={{ backgroundColor: tag.color || '#6B7280' }}
            ></span>
            <h1 className="text-3xl font-bold">Tag: {tag.name}</h1>
          </div>
          
          {tag.description && (
            <p className="text-gray-600 mt-2">{tag.description}</p>
          )}
          
          <div className="text-sm text-gray-500 mt-3">
            {pagination.totalItems} {pagination.totalItems === 1 ? 'post encontrado' : 'posts encontrados'}
          </div>
        </header>

        {posts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-xl text-gray-500">
              Nenhum post encontrado com esta tag
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {posts.map(post => (
              <article key={post.id} className="border-b pb-6">
                <Link to={`/posts/${post.slug}`} className="group">
                  <h2 className="text-2xl font-bold mb-2 group-hover:text-blue-600 transition">
                    {post.title}
                  </h2>
                </Link>
                
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <span>
                    {post.author?.name || 'Autor desconhecido'} • {' '}
                    {format(new Date(post.publishedAt), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </span>
                  
                  {post.readTime && (
                    <span className="ml-3">
                      • {post.readTime} min de leitura
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 mb-3">{post.excerpt}</p>
                
                <div className="flex flex-wrap gap-2">
                  {post.tags?.map(tag => (
                    <Link
                      key={tag.id}
                      to={`/tag/${tag.slug}`}
                      className="text-xs px-2 py-1 rounded"
                      style={{
                        backgroundColor: tag.color ? `${tag.color}20` : '#e5e7eb',
                        color: tag.color || '#374151'
                      }}
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
                
                <Link 
                  to={`/posts/${post.slug}`}
                  className="inline-block mt-3 text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Ler mais →
                </Link>
              </article>
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <PaginationControls
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            className="mt-8"
          />
        )}
      </div>
    </>
  );
};

export default TagView;
